import { NotationContent, Row, Section, Vector } from "../model";

/**
 * Internal helpers for rebuilding the document spine immutably.
 *
 * WHY THIS EXISTS: the original code did `{...this.file}` — a ONE-LEVEL copy —
 * and then mutated the nested arrays in place (`cell[i] = v`). Three problems
 * came from that: `React.memo` could never work (and would have broken
 * rendering if added), any retained snapshot silently aliased the live
 * document, and undo/redo was impossible. These helpers replace every node on
 * the path from the root down to the thing being changed, and share everything
 * off the path.
 *
 * Ops are generic over `T extends NotationContent` so a caller can pass a full
 * document (with Firestore metadata attached) and get the same type back.
 */

/** Immutably replace one element of an array. */
export function replaceAt<T>(arr: T[], index: number, value: T): T[] {
    return [...arr.slice(0, index), value, ...arr.slice(index + 1)];
}

/** Immutably remove one element of an array. */
export function removeAt<T>(arr: T[], index: number): T[] {
    return [...arr.slice(0, index), ...arr.slice(index + 1)];
}

/** Immutably insert an element at a position (appends when position is undefined). */
export function insertAt<T>(arr: T[], value: T, position?: number): T[] {
    if (typeof position === "undefined") return [...arr, value];
    return [...arr.slice(0, position), value, ...arr.slice(position)];
}

/** Immutably swap two elements. */
export function swap<T>(arr: T[], a: number, b: number): T[] {
    const next = [...arr];
    const tmp = next[a];
    next[a] = next[b];
    next[b] = tmp;
    return next;
}

/** Replace `sections` on the document, preserving all other fields. */
export function withSections<T extends NotationContent>(
    content: T,
    sections: Section[]
): T {
    return { ...content, sections };
}

/** Rebuild the spine down to one section. */
export function mapSection<T extends NotationContent>(
    content: T,
    sectionIndex: number,
    fn: (section: Section) => Section
): T {
    const section = content.sections[sectionIndex];
    if (!section) return content;
    return withSections(
        content,
        replaceAt(content.sections, sectionIndex, fn(section))
    );
}

/** Rebuild the spine down to one row. */
export function mapRow<T extends NotationContent>(
    content: T,
    sectionIndex: number,
    rowIndex: number,
    fn: (row: Row) => Row
): T {
    return mapSection(content, sectionIndex, (section) => {
        const row = section.rows[rowIndex];
        if (!row) return section;
        return { ...section, rows: replaceAt(section.rows, rowIndex, fn(row)) };
    });
}

/** Rebuild the spine down to one vector (matra). */
export function mapVector<T extends NotationContent>(
    content: T,
    sectionIndex: number,
    rowIndex: number,
    vectorIndex: number,
    fn: (vector: Vector) => Vector
): T {
    return mapRow(content, sectionIndex, rowIndex, (row) => {
        const vector = row.vectors[vectorIndex];
        if (!vector) return row;
        return {
            ...row,
            vectors: replaceAt(row.vectors, vectorIndex, fn(vector)),
        };
    });
}

/** Apply a transform to every vector in a row. */
export function mapAllVectors<T extends NotationContent>(
    content: T,
    sectionIndex: number,
    rowIndex: number,
    fn: (vector: Vector) => Vector
): T {
    return mapRow(content, sectionIndex, rowIndex, (row) => ({
        ...row,
        vectors: row.vectors.map(fn),
    }));
}
